import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  IconButton,
  Toolbar,
  Typography,
  TextField,
  Button,
  Slider,
  Tooltip,
  Divider
} from '@mui/material';
import {
  ArrowForward as ArrowIcon,
  TextFields as TextIcon,
  RadioButtonUnchecked as CircleIcon,
  CropSquare as RectangleIcon,
  Undo as UndoIcon,
  Redo as RedoIcon,
  Save as SaveIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { ImageAnnotation, ImageAnnotationService } from '../services/imageUploadService';

interface ImageAnnotationEditorProps {
  imageUrl: string;
  annotations: ImageAnnotation[];
  onAnnotationsChange: (annotations: ImageAnnotation[]) => void;
  onSave?: () => void;
  width?: number;
  height?: number;
}

type AnnotationTool = 'arrow' | 'text' | 'circle' | 'rectangle' | 'select';

const ImageAnnotationEditor: React.FC<ImageAnnotationEditorProps> = ({
  imageUrl,
  annotations,
  onAnnotationsChange,
  onSave,
  width = 800,
  height = 600
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  
  const [currentTool, setCurrentTool] = useState<AnnotationTool>('select');
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPoint, setStartPoint] = useState<{ x: number; y: number } | null>(null);
  const [currentAnnotation, setCurrentAnnotation] = useState<ImageAnnotation | null>(null);
  
  // إعدادات الرسم
  const [drawingSettings, setDrawingSettings] = useState({
    color: '#ff0000',
    strokeWidth: 3,
    fontSize: 16,
    text: ''
  });

  // إعدادات النص
  const [textInput, setTextInput] = useState('');
  const [showTextInput, setShowTextInput] = useState(false);
  const [textPosition, setTextPosition] = useState<{ x: number; y: number } | null>(null);

  // تاريخ التعديلات للتراجع والإعادة
  const [history, setHistory] = useState<ImageAnnotation[][]>([annotations]);
  const [historyIndex, setHistoryIndex] = useState(0);

  // تحميل الصورة وإعداد الكانفاس
  useEffect(() => {
    const canvas = canvasRef.current;
    const image = imageRef.current;
    
    if (canvas && image && imageUrl) {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        image.onload = () => {
          // تحديد أبعاد الكانفاس بناءً على الصورة
          const aspectRatio = image.naturalWidth / image.naturalHeight;
          let canvasWidth = width;
          let canvasHeight = height;
          
          if (aspectRatio > 1) {
            canvasHeight = width / aspectRatio;
          } else {
            canvasWidth = height * aspectRatio;
          }
          
          canvas.width = canvasWidth;
          canvas.height = canvasHeight;
          
          redrawCanvas();
        };
        image.src = imageUrl;
      }
    }
  }, [imageUrl, width, height]);

  // إعادة رسم الكانفاس
  useEffect(() => {
    redrawCanvas();
  }, [annotations]);

  const redrawCanvas = () => {
    const canvas = canvasRef.current;
    const image = imageRef.current;
    const ctx = canvas?.getContext('2d');
    
    if (!canvas || !ctx || !image) return;

    // مسح الكانفاس
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // رسم الصورة
    if (image.complete) {
      ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
      
      // رسم التعليقات
      annotations.forEach(annotation => {
        drawAnnotation(ctx, annotation);
      });
      
      // رسم التعليق الحالي إذا كان موجوداً
      if (currentAnnotation) {
        drawAnnotation(ctx, currentAnnotation);
      }
    }
  };

  const drawAnnotation = (ctx: CanvasRenderingContext2D, annotation: ImageAnnotation) => {
    ctx.strokeStyle = annotation.color;
    ctx.fillStyle = annotation.color;
    ctx.lineWidth = annotation.strokeWidth || 3;
    ctx.font = `${annotation.fontSize || 16}px Arial`;

    switch (annotation.type) {
      case 'arrow':
        drawArrow(ctx, annotation);
        break;
      case 'text':
        drawText(ctx, annotation);
        break;
      case 'circle':
        drawCircle(ctx, annotation);
        break;
      case 'rectangle':
        drawRectangle(ctx, annotation);
        break;
    }
  };

  const drawArrow = (ctx: CanvasRenderingContext2D, annotation: ImageAnnotation) => {
    if (!annotation.endX || !annotation.endY) return;
    
    const { x, y, endX, endY } = annotation;
    
    // رسم الخط
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(endX, endY);
    ctx.stroke();
    
    // رسم رأس السهم
    const angle = Math.atan2(endY - y, endX - x);
    const arrowLength = 15;
    const arrowAngle = Math.PI / 6;
    
    ctx.beginPath();
    ctx.moveTo(endX, endY);
    ctx.lineTo(
      endX - arrowLength * Math.cos(angle - arrowAngle),
      endY - arrowLength * Math.sin(angle - arrowAngle)
    );
    ctx.moveTo(endX, endY);
    ctx.lineTo(
      endX - arrowLength * Math.cos(angle + arrowAngle),
      endY - arrowLength * Math.sin(angle + arrowAngle)
    );
    ctx.stroke();
  };

  const drawText = (ctx: CanvasRenderingContext2D, annotation: ImageAnnotation) => {
    if (!annotation.text) return;
    
    ctx.fillText(annotation.text, annotation.x, annotation.y);
  };

  const drawCircle = (ctx: CanvasRenderingContext2D, annotation: ImageAnnotation) => {
    if (!annotation.width || !annotation.height) return;
    
    const centerX = annotation.x + annotation.width / 2;
    const centerY = annotation.y + annotation.height / 2;
    const radius = Math.min(annotation.width, annotation.height) / 2;
    
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
    ctx.stroke();
  };

  const drawRectangle = (ctx: CanvasRenderingContext2D, annotation: ImageAnnotation) => {
    if (!annotation.width || !annotation.height) return;
    
    ctx.beginPath();
    ctx.rect(annotation.x, annotation.y, annotation.width, annotation.height);
    ctx.stroke();
  };

  const getCanvasCoordinates = (event: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    return {
      x: (event.clientX - rect.left) * scaleX,
      y: (event.clientY - rect.top) * scaleY
    };
  };

  const handleMouseDown = (event: React.MouseEvent<HTMLCanvasElement>) => {
    const coords = getCanvasCoordinates(event);
    setStartPoint(coords);
    setIsDrawing(true);

    if (currentTool === 'text') {
      setTextPosition(coords);
      setShowTextInput(true);
      return;
    }

    // إنشاء تعليق جديد
    let newAnnotation: ImageAnnotation;
    
    switch (currentTool) {
      case 'arrow':
        newAnnotation = ImageAnnotationService.createArrowAnnotation(
          coords.x, coords.y, coords.x, coords.y, 
          drawingSettings.color, drawingSettings.strokeWidth
        );
        break;
      case 'circle':
        newAnnotation = ImageAnnotationService.createCircleAnnotation(
          coords.x, coords.y, 0, 0,
          drawingSettings.color, drawingSettings.strokeWidth
        );
        break;
      case 'rectangle':
        newAnnotation = ImageAnnotationService.createRectangleAnnotation(
          coords.x, coords.y, 0, 0,
          drawingSettings.color, drawingSettings.strokeWidth
        );
        break;
      default:
        return;
    }
    
    setCurrentAnnotation(newAnnotation);
  };

  const handleMouseMove = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing || !startPoint || !currentAnnotation) return;
    
    const coords = getCanvasCoordinates(event);
    
    let updatedAnnotation = { ...currentAnnotation };
    
    switch (currentTool) {
      case 'arrow':
        updatedAnnotation.endX = coords.x;
        updatedAnnotation.endY = coords.y;
        break;
      case 'circle':
      case 'rectangle':
        updatedAnnotation.width = Math.abs(coords.x - startPoint.x);
        updatedAnnotation.height = Math.abs(coords.y - startPoint.y);
        updatedAnnotation.x = Math.min(startPoint.x, coords.x);
        updatedAnnotation.y = Math.min(startPoint.y, coords.y);
        break;
    }
    
    setCurrentAnnotation(updatedAnnotation);
  };

  const handleMouseUp = () => {
    if (currentAnnotation && isDrawing) {
      const newAnnotations = [...annotations, currentAnnotation];
      onAnnotationsChange(newAnnotations);
      addToHistory(newAnnotations);
      setCurrentAnnotation(null);
    }
    
    setIsDrawing(false);
    setStartPoint(null);
  };

  const handleTextSubmit = () => {
    if (textInput && textPosition) {
      const textAnnotation = ImageAnnotationService.createTextAnnotation(
        textPosition.x, textPosition.y, textInput,
        drawingSettings.color, drawingSettings.fontSize
      );
      
      const newAnnotations = [...annotations, textAnnotation];
      onAnnotationsChange(newAnnotations);
      addToHistory(newAnnotations);
    }
    
    setShowTextInput(false);
    setTextInput('');
    setTextPosition(null);
  };

  const addToHistory = (newAnnotations: ImageAnnotation[]) => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(newAnnotations);
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  };

  const undo = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      onAnnotationsChange(history[newIndex]);
    }
  };

  const redo = () => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      onAnnotationsChange(history[newIndex]);
    }
  };

  const clearAll = () => {
    onAnnotationsChange([]);
    addToHistory([]);
  };

  return (
    <Paper sx={{ p: 2 }}>
      {/* شريط الأدوات */}
      <Toolbar sx={{ mb: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
        <Typography variant="h6" sx={{ mr: 2 }}>
          محرر الصور
        </Typography>
        
        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
        
        {/* أدوات الرسم */}
        <Tooltip title="تحديد">
          <IconButton
            color={currentTool === 'select' ? 'primary' : 'default'}
            onClick={() => setCurrentTool('select')}
          >
            <ArrowIcon />
          </IconButton>
        </Tooltip>
        
        <Tooltip title="سهم">
          <IconButton
            color={currentTool === 'arrow' ? 'primary' : 'default'}
            onClick={() => setCurrentTool('arrow')}
          >
            <ArrowIcon />
          </IconButton>
        </Tooltip>
        
        <Tooltip title="نص">
          <IconButton
            color={currentTool === 'text' ? 'primary' : 'default'}
            onClick={() => setCurrentTool('text')}
          >
            <TextIcon />
          </IconButton>
        </Tooltip>
        
        <Tooltip title="دائرة">
          <IconButton
            color={currentTool === 'circle' ? 'primary' : 'default'}
            onClick={() => setCurrentTool('circle')}
          >
            <CircleIcon />
          </IconButton>
        </Tooltip>
        
        <Tooltip title="مستطيل">
          <IconButton
            color={currentTool === 'rectangle' ? 'primary' : 'default'}
            onClick={() => setCurrentTool('rectangle')}
          >
            <RectangleIcon />
          </IconButton>
        </Tooltip>
        
        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
        
        {/* أدوات التحكم */}
        <Tooltip title="تراجع">
          <IconButton onClick={undo} disabled={historyIndex <= 0}>
            <UndoIcon />
          </IconButton>
        </Tooltip>
        
        <Tooltip title="إعادة">
          <IconButton onClick={redo} disabled={historyIndex >= history.length - 1}>
            <RedoIcon />
          </IconButton>
        </Tooltip>
        
        <Tooltip title="مسح الكل">
          <IconButton onClick={clearAll}>
            <ClearIcon />
          </IconButton>
        </Tooltip>
        
        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
        
        {/* إعدادات الرسم */}
        <Box display="flex" alignItems="center" gap={2}>
          <input
            type="color"
            value={drawingSettings.color}
            onChange={(e) => setDrawingSettings({ ...drawingSettings, color: e.target.value })}
            style={{ width: 40, height: 30, border: 'none', borderRadius: 4 }}
          />
          
          <Box sx={{ minWidth: 100 }}>
            <Typography variant="caption">سمك الخط: {drawingSettings.strokeWidth}</Typography>
            <Slider
              value={drawingSettings.strokeWidth}
              onChange={(_, value) => setDrawingSettings({ ...drawingSettings, strokeWidth: value as number })}
              min={1}
              max={10}
              size="small"
            />
          </Box>
          
          <Box sx={{ minWidth: 100 }}>
            <Typography variant="caption">حجم النص: {drawingSettings.fontSize}</Typography>
            <Slider
              value={drawingSettings.fontSize}
              onChange={(_, value) => setDrawingSettings({ ...drawingSettings, fontSize: value as number })}
              min={10}
              max={48}
              size="small"
            />
          </Box>
        </Box>
        
        <Box sx={{ flexGrow: 1 }} />
        
        {onSave && (
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={onSave}
          >
            حفظ
          </Button>
        )}
      </Toolbar>

      {/* منطقة الرسم */}
      <Box sx={{ position: 'relative', display: 'inline-block' }}>
        <canvas
          ref={canvasRef}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          style={{
            border: '1px solid #ccc',
            cursor: currentTool === 'select' ? 'default' : 'crosshair',
            maxWidth: '100%',
            height: 'auto'
          }}
        />
        
        {/* صورة مخفية للتحميل */}
        <img
          ref={imageRef}
          src={imageUrl}
          alt="للتعديل"
          style={{ display: 'none' }}
        />
      </Box>

      {/* حوار إدخال النص */}
      {showTextInput && (
        <Box
          sx={{
            position: 'absolute',
            top: textPosition?.y || 0,
            left: textPosition?.x || 0,
            bgcolor: 'white',
            p: 2,
            border: 1,
            borderColor: 'grey.300',
            borderRadius: 1,
            zIndex: 1000
          }}
        >
          <TextField
            autoFocus
            label="النص"
            value={textInput}
            onChange={(e) => setTextInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleTextSubmit();
              }
            }}
            size="small"
          />
          <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
            <Button size="small" onClick={handleTextSubmit}>إضافة</Button>
            <Button size="small" onClick={() => setShowTextInput(false)}>إلغاء</Button>
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default ImageAnnotationEditor;
